import { useEffect } from "react";
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";
import { userAtom } from "recoil/userAtom";
import useGetCertainEventFromApp from "./useGetCertainEventFromApp";
import { loginHintAtom, LoginMethod } from "recoil/loginHintAtom";

const VALID_METHODS: LoginMethod[] = ["apple", "google", "kakao"];
const toMethod = (v?: string): LoginMethod | undefined =>
  VALID_METHODS.find((m) => m === v?.toLowerCase());

/**
 * "마지막 로그인 수단"(loginHintAtom)을 갱신한다. LoginHintReceiver 로 앱 전역에서 마운트.
 * 소스:
 *  (1) OneLink 딥링크(event: "lastLoginMethod") — 퍼널 진입 시 갱신.
 *  (2) 실제 로그인 수단(userAtom.authenticateType) — apple/google/kakao 면 기록,
 *      그 외(이메일 등)면 라벨 제거(fallback = 표시 안 함).
 * 값은 loginHintAtom(localStorage 영속)이라 로그아웃/재시작에도 유지된다.
 */
export default function useReceiveLoginHint() {
  const setLoginHint = useSetRecoilState(loginHintAtom);
  const resetLoginHint = useResetRecoilState(loginHintAtom);
  const message = useGetCertainEventFromApp({ eventName: "lastLoginMethod" });
  const authenticateType = useRecoilValue(userAtom)?.authenticateType;

  // (1) OneLink 딥링크로 전달된 값
  useEffect(() => {
    const method = toMethod(message?.params?.value);
    if (method) setLoginHint(method);
  }, [message, setLoginHint]);

  // (2) 실제 로그인 수단 (로그아웃/미로그인 시 authenticateType 없음 → 직전 값 유지)
  useEffect(() => {
    if (!authenticateType) return;
    const method = toMethod(authenticateType);
    method ? setLoginHint(method) : resetLoginHint();
  }, [authenticateType, setLoginHint, resetLoginHint]);
}
